import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  Check as ApproveIcon,
  Check,
  Close as RejectIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { UserContext } from "../context/UserContext.js";
import AfeService from "../service/afeService.js";
import Q8NoData from "../components/Q8Nodata.js";

const AfePendingChanges = () => {
  // State
  const [pendingChanges, setPendingChanges] = useState([]);
  const [expandedRecords, setExpandedRecords] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
  });
  const [loading, _] = useState(true);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null,
    change: null,
    title: "",
    message: "",
  });
  // NUOVO: Stato per l'opzione di blocco
  const [lockAfterApproval, setLockAfterApproval] = useState(false);

  const { user } = useContext(UserContext);

  // Helper functions
  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type });
  };

  const toggleExpandRecord = (id) => {
    setExpandedRecords((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const loadPendingChanges = async () => {
    try {
      const response = await AfeService.getPendingRecordChanges();

      // Parse JSON strings to objects
      const processedChanges = response.map((change) => ({
        ...change,
        originalRecord: JSON.parse(change.originalRecordJson),
        modifiedRecord: JSON.parse(change.modifiedRecordJson),
      }));

      setPendingChanges(processedChanges);
    } catch (error) {
      console.error("Error loading pending changes:", error);
      showNotification("Error loading pending changes", "error");
    }
  };

  // Load data when component mounts
  useEffect(() => {
    loadPendingChanges();
  }, []);

  const openConfirmDialog = (action, change) => {
    const isApprove = action === "approve";
    setConfirmDialog({
      open: true,
      action,
      change,
      title: isApprove ? "Approve Change" : "Reject Change",
      message: isApprove
        ? `Are you sure you want to approve all changes to AFE ${change.afeCode}?`
        : `Are you sure you want to reject all changes to AFE ${change.afeCode}?`,
    });

    // Reset lock option when opening dialog
    setLockAfterApproval(false);
  };

  const openApproveAllDialog = () => {
    const pendingCount = pendingChanges.filter(
      (change) => change.status === "pending"
    ).length;

    setConfirmDialog({
      open: true,
      action: "approveAll",
      change: null,
      title: "Approva Tutte le Modifiche",
      message: `Sei sicuro di voler approvare tutte le ${pendingCount} modifiche pendenti?`,
    });

    // Reset lock option when opening dialog
    setLockAfterApproval(false);
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      ...confirmDialog,
      open: false,
    });
  };

  const handleConfirmAction = async () => {
    const { action, change } = confirmDialog;
    closeConfirmDialog();

    try {
      if (action === "approveAll") {
        // Ottieni solo le modifiche con stato "pending"
        const pendingItems = pendingChanges.filter(
          (item) => item.status === "pending"
        );

        if (pendingItems.length === 0) {
          showNotification("Nessuna modifica pendente da approvare", "warning");
          return;
        }

        // Imposta lo stato di caricamento
        setBulkActionLoading(true);

        // Mostra un messaggio di caricamento
        showNotification(
          "Approvazione di tutte le modifiche in corso...",
          "info"
        );

        // Approva ogni modifica in sequenza
        let approvedCount = 0;
        for (const item of pendingItems) {
          const payload = {
            changeId: item.id,
            handledBy: user.email,
            notes: "Bulk approval",
            lockRecord: lockAfterApproval, // NUOVO: Aggiungi il flag di blocco
          };

          await AfeService.approveRecordPendingChange(item.id, payload);
          approvedCount++;
        }

        showNotification(
          `${approvedCount} modifiche approvate con successo` +
            (lockAfterApproval
              ? " e record bloccati per modifiche future"
              : ""),
          "success"
        );
      } else if (action === "approve") {
        await AfeService.approveRecordPendingChange(change.id, {
          changeId: change.id,
          handledBy: user.email,
          notes: lockAfterApproval
            ? "Approvato e bloccato per modifiche future"
            : "",
          lockRecord: lockAfterApproval, // NUOVO: Aggiungi il flag di blocco
        });

        showNotification(
          `Modifiche a AFE ${change.afeCode} approvate con successo` +
            (lockAfterApproval
              ? " e record bloccato per modifiche future"
              : ""),
          "success"
        );
      } else {
        await AfeService.rejectRecordPendingChange(change.id, {
          changeId: change.id,
          handledBy: user.email,
          notes: "",
        });
        showNotification(`Modifiche a AFE ${change.afeCode} rifiutate`, "info");
      }

      // Ricarica i dati
      await loadPendingChanges();
    } catch (error) {
      console.error(
        `Errore durante ${
          action === "approveAll"
            ? "l'approvazione di tutte le modifiche"
            : action === "approve"
            ? "l'approvazione"
            : "il rifiuto"
        }:`,
        error
      );
      showNotification(
        `Errore durante ${
          action === "approveAll"
            ? "l'approvazione di tutte le modifiche"
            : action === "approve"
            ? "l'approvazione"
            : "il rifiuto"
        }`,
        "error"
      );
    } finally {
      // Resetta lo stato di caricamento
      setBulkActionLoading(false);
    }
  };

  // Function to detect and highlight differences between original and modified records
  const isDifferent = (key, originalRecord, modifiedRecord) => {
    if (!originalRecord || !modifiedRecord) return false;

    // Converti null e undefined in stringhe vuote per un confronto uniforme
    const originalValue =
      originalRecord[key] === null || originalRecord[key] === undefined
        ? ""
        : typeof originalRecord[key] === "number"
        ? originalRecord[key].toString()
        : originalRecord[key];

    const modifiedValue =
      modifiedRecord[key] === null || modifiedRecord[key] === undefined
        ? ""
        : typeof modifiedRecord[key] === "number"
        ? modifiedRecord[key].toString()
        : modifiedRecord[key];

    return originalValue !== modifiedValue;
  };

  // Function to format values for display
  const formatValue = (key, value) => {
    if (value === null || value === undefined) return "-";

    // Format monetary values
    if (
      ["totalAfe", "capexYear", "spesoProgressivo"].includes(key) &&
      typeof value === "number"
    ) {
      return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
      }).format(value);
    }

    return value.toString();
  };

  // Get the keys to display from the record
  const getKeysToDisplay = (record) => {
    if (!record) return [];

    // Define keys to display in the order you want
    const orderedKeys = [
      "afeCode",
      "afeDesc",
      "afeFyscalYear",
      "fiscalYearClosure",
      "fiscalYearRecord",
      "responsible",
      "programId",
      "userResponsible",
      "companyCode",
      "ou",
      "localKPI",
      "catAfe",
      "totalAfe",
      "capexYear",
      "spesoProgressivo",
      "localCorporate",
      "raggruppamento1",
      "raggruppamento2",
      "raggruppamento3",
    ];

    // Filter out keys that don't exist in the record
    return orderedKeys.filter((key) => record.hasOwnProperty(key));
  };

  // Function to get a more user-friendly field name
  const getFieldDisplayName = (key) => {
    const fieldNameMap = {
      afeCode: "AFE Code",
      afeDesc: "Description",
      afeFyscalYear: "Fiscal Year",
      fiscalYearClosure: "Closure Year",
      fiscalYearRecord: "Fiscal Year Record",
      responsible: "Responsible",
      programId: "Program ID",
      userResponsible: "User Responsible",
      companyCode: "Company Code",
      ou: "OU",
      localKPI: "Local/KPI Approval",
      catAfe: "Category",
      totalAfe: "Total AFE",
      capexYear: "CAPEX Year",
      spesoProgressivo: "Speso Progressivo",
      localCorporate: "Local/Corporate",
      raggruppamento1: "Raggruppamento 1",
      raggruppamento2: "Raggruppamento 2",
      raggruppamento3: "Raggruppamento 3",
    };

    return fieldNameMap[key] || key;
  };

  return (
    <Box
      sx={{ height: "100vh", display: "flex", flexDirection: "column", p: 2 }}
    >
      <Q8Breadcrumbs
        links={[
          { label: "CAPEX", href: "/capex" },
          { label: "Modifiche pendenti", href: "/capex/pending" },
        ]}
        variant="primary"
      />
      <Box sx={{ mt: 3, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ color: "primary.main" }}>
            AFE Modifiche Pendenti
          </Typography>

          {pendingChanges.filter((change) => change.status === "pending")
            .length > 0 && (
            <Q8Button
              variant="primary"
              label={
                bulkActionLoading ? "Approvazione in corso..." : "Approva Tutte"
              }
              onClick={openApproveAllDialog}
              size="medium"
              startIcon={<Check />}
              disabled={bulkActionLoading}
            />
          )}
        </Box>

        <Typography variant="body1" sx={{ mt: 1 }}>
          Rivedi ed approva o rifiuta le modifiche proposte agli AFE records.
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Codice AFE</TableCell>
              <TableCell>Modificato da</TableCell>
              <TableCell>Modificato il</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingChanges.map((change) => (
              <React.Fragment key={change.id}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleExpandRecord(change.id)}
                    >
                      {expandedRecords[change.id] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{change.afeCode}</TableCell>
                  <TableCell>{change.modifiedBy}</TableCell>
                  <TableCell>
                    {new Date(change.modifiedAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        change.status === "pending"
                          ? "In attesa"
                          : change.status === "approved"
                          ? "Approvato"
                          : ""
                      }
                      color={
                        change.status === "pending"
                          ? "warning"
                          : change.status === "approved"
                          ? "success"
                          : "error"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {change.status === "pending" && (
                      <>
                        <Tooltip title="Approve Changes">
                          <IconButton
                            color="success"
                            onClick={() => openConfirmDialog("approve", change)}
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject Changes">
                          <IconButton
                            color="error"
                            onClick={() => openConfirmDialog("reject", change)}
                          >
                            <RejectIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={6}
                  >
                    <Collapse
                      in={expandedRecords[change.id]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ m: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Change Details
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                                }}
                              >
                                Field
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                                }}
                              >
                                Original Value
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                                }}
                              >
                                Modified Value
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getKeysToDisplay(change.originalRecord).map(
                              (key) => {
                                const isDiff = isDifferent(
                                  key,
                                  change.originalRecord,
                                  change.modifiedRecord
                                );
                                return (
                                  <TableRow
                                    key={key}
                                    sx={{
                                      backgroundColor: isDiff
                                        ? "rgba(255, 243, 224, 0.5)"
                                        : "inherit",
                                    }}
                                  >
                                    <TableCell>
                                      {getFieldDisplayName(key)}
                                    </TableCell>
                                    <TableCell>
                                      {formatValue(
                                        key,
                                        change.originalRecord[key]
                                      )}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontWeight: isDiff ? "bold" : "normal",
                                        color: isDiff
                                          ? "primary.main"
                                          : "inherit",
                                      }}
                                    >
                                      {formatValue(
                                        key,
                                        change.modifiedRecord[key]
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              }
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        {pendingChanges.length === 0 && <Q8NoData />}
      </TableContainer>
      {/* Confirmation Dialog - MODIFICATO per includere l'opzione di blocco */}
      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>

          {/* Opzione per bloccare il record */}
          {/* {(confirmDialog.action === "approve" ||
            confirmDialog.action === "approveAll") && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={lockAfterApproval}
                  onChange={(e) => setLockAfterApproval(e.target.checked)}
                  icon={<LockIcon />}
                  checkedIcon={<LockIcon />}
                />
              }
              label="Blocca questo record per modifiche future"
              sx={{ mt: 2, display: "flex", alignItems: "center" }}
            />
          )} */}
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={closeConfirmDialog}
          />
          <Q8Button
            variant="primary"
            label="Conferma"
            onClick={handleConfirmAction}
          />
        </DialogActions>
      </Dialog>
      {/* Snackbar for notifications */}
      <Q8SnackBar
        open={snackbar.open}
        type={snackbar.type}
        title="Notification"
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default AfePendingChanges;
